// HP, COMP, ANR, HDS, EE: equity functions on Yahoo chart and summary data.

import { LineSeries, type UTCTimestamp } from "../../vendor/lwc.mjs";
import { chart, summary, type Chart, type Summary } from "../api";
import { COLORS, makeChart } from "../chart";
import { clear, download, errorBox, h, kv, loading, rangeBar, toggles } from "../dom";
import * as f from "../fmt";
import { register, type Ctx } from "../registry";
import { display, resolve } from "../securities";
import { DataTable } from "../table";

const day = (t: number, off = 0) => Math.floor((t + off) / 86400) * 86400;

// --- HP ---------------------------------------------------------------------

const HP_RANGES = ["1M", "3M", "6M", "YTD", "1Y", "2Y", "5Y", "10Y", "MAX"] as const;
type HpRange = (typeof HP_RANGES)[number];
const HP_PERIODS = [
  ["1d", "Daily"],
  ["1wk", "Weekly"],
  ["1mo", "Monthly"],
] as const;
type Period = (typeof HP_PERIODS)[number][0];

interface Bar {
  t: number;
  o: number | null;
  h: number | null;
  l: number | null;
  c: number;
  adj: number | null;
  v: number | null;
  chg?: number;
  pct?: number;
  note?: string;
}

function bars(c: Chart): Bar[] {
  const off = c.meta.gmtoffset ?? 0;
  const notes = new Map<number, string>();
  for (const d of c.dividends) notes.set(day(d.t, off), `Div ${f.price(d.amount, 4)}`);
  for (const s of c.splits) notes.set(day(s.t, off), `Split ${s.splitRatio}`);
  return c.t.map((t, i) => {
    const prev = i > 0 ? c.c[i - 1] : undefined;
    const d = day(t, off);
    return {
      t: d,
      o: c.o[i],
      h: c.h[i],
      l: c.l[i],
      c: c.c[i],
      adj: c.adj?.[i] ?? null,
      v: c.v[i],
      chg: prev !== undefined ? c.c[i] - prev : undefined,
      pct: prev ? (c.c[i] / prev - 1) * 100 : undefined,
      note: notes.get(d),
    };
  });
}

/// Annualized volatility of log returns.
function vol(closes: number[], perYear: number): number | undefined {
  if (closes.length < 3) return undefined;
  const r = closes.slice(1).map((c, i) => Math.log(c / closes[i]));
  const mean = r.reduce((a, b) => a + b, 0) / r.length;
  const v = r.reduce((a, b) => a + (b - mean) ** 2, 0) / (r.length - 1);
  return Math.sqrt(v * perYear) * 100;
}

function maxDrawdown(closes: number[]): number | undefined {
  if (!closes.length) return undefined;
  let peak = closes[0];
  let dd = 0;
  for (const c of closes) {
    peak = Math.max(peak, c);
    dd = Math.min(dd, c / peak - 1);
  }
  return dd * 100;
}

function runHP(ctx: Ctx): void {
  const sym = ctx.sec!;
  let range: HpRange = (HP_RANGES as readonly string[]).includes(ctx.args[0]) ? (ctx.args[0] as HpRange) : "3M";
  let period: Period = "1d";
  let last: Bar[] = [];
  const toolbar = h("div", { class: "toolbar" });
  const stats = h("div", { class: "pad" });
  const table = new DataTable<Bar>(
    [
      { label: "Date", get: (b) => b.t, fmt: (v) => f.utcDate(v), cls: () => "amber" },
      { label: "Open", get: (b) => b.o, fmt: (v) => f.price(v), num: true },
      { label: "High", get: (b) => b.h, fmt: (v) => f.price(v), num: true },
      { label: "Low", get: (b) => b.l, fmt: (v) => f.price(v), num: true },
      { label: "Close", get: (b) => b.c, fmt: (v) => f.price(v), num: true },
      { label: "Adj close", get: (b) => b.adj, fmt: (v) => f.price(v), num: true, title: "Adjusted for splits and dividends" },
      { label: "Change", get: (b) => b.chg, fmt: (v) => f.chg(v), cls: (v) => f.cls(v), num: true },
      { label: "%Chg", get: (b) => b.pct, fmt: (v) => f.pct(v), cls: (v) => f.cls(v), num: true },
      { label: "Volume", get: (b) => b.v, fmt: (v) => f.int(v), num: true },
      { label: "", get: (b) => b.note ?? "", cls: () => "yellow", sort: false },
    ],
  );
  const csv = () => {
    const rows = [["date", "open", "high", "low", "close", "adj_close", "volume"].join(",")];
    for (const b of last) rows.push([f.utcDate(b.t), b.o, b.h, b.l, b.c, b.adj ?? "", b.v ?? ""].join(","));
    download(`${sym}-${period}-${range}.csv`, rows.join("\n") + "\n");
  };
  const drawToolbar = () =>
    clear(
      toolbar,
      ...toggles(HP_RANGES, range, (v) => ((range = v), drawToolbar(), load())),
      h("span", { class: "sep" }),
      ...toggles(HP_PERIODS, period, (v) => ((period = v), drawToolbar(), load())),
      h("span", { class: "sep" }),
      h("button", { onclick: csv, title: "Download as CSV" }, "CSV"),
    );
  const load = async () => {
    clear(stats, loading());
    try {
      const r = await chart(sym, range.toLowerCase().replace(/^(\d+)m$/, "$1mo"), period, ctx.signal);
      last = bars(r.data);
      const closes = last.map((b) => b.c);
      const hi = Math.max(...last.map((b) => b.h ?? b.c));
      const lo = Math.min(...last.map((b) => b.l ?? b.c));
      const ret = closes.length > 1 ? (closes[closes.length - 1] / closes[0] - 1) * 100 : undefined;
      const perYear = period === "1d" ? 252 : period === "1wk" ? 52 : 12;
      const vols = last.map((b) => b.v ?? 0);
      clear(
        stats,
        h(
          "div",
          { class: "cols" },
          kv([
            ["Period return", h("span", { class: f.cls(ret) }, f.pct(ret))],
            ["High / low", `${f.price(hi)} / ${f.price(lo)}`],
            ["Position", rangeBar(lo, hi, closes[closes.length - 1], 90)],
          ]),
          kv([
            ["Volatility (ann.)", f.num(vol(closes, perYear)) !== undefined ? `${f.fixedN(vol(closes, perYear), 1)}%` : f.NA],
            ["Max drawdown", f.pct(maxDrawdown(closes), 1)],
            ["Avg volume", f.big(vols.reduce((a, b) => a + b, 0) / (vols.length || 1))],
          ]),
        ),
      );
      table.set([...last].reverse());
      ctx.panel.status(`Yahoo · ${last.length} ${HP_PERIODS.find((p) => p[0] === period)![1].toLowerCase()} bars`, r.meta);
    } catch (e) {
      if (!ctx.signal.aborted) clear(stats, errorBox(e));
    }
  };
  drawToolbar();
  clear(ctx.body, toolbar, stats, table.el);
  load();
}

// --- COMP -------------------------------------------------------------------

const COMP_RANGES: [string, string, string][] = [
  ["1M", "1mo", "1d"],
  ["3M", "3mo", "1d"],
  ["6M", "6mo", "1d"],
  ["YTD", "ytd", "1d"],
  ["1Y", "1y", "1d"],
  ["2Y", "2y", "1d"],
  ["5Y", "5y", "1wk"],
  ["10Y", "10y", "1wk"],
  ["MAX", "max", "1mo"],
];
const COMP_RANGE = /^(1M|3M|6M|YTD|1Y|2Y|5Y|10Y|MAX)$/;

interface CompRow {
  sym: string;
  name: string;
  color: string;
  closes: number[];
  times: number[];
  ret?: number;
  vol?: number;
  dd?: number;
  corr?: number;
}

function correlation(a: Map<number, number>, b: Map<number, number>): number | undefined {
  const days = [...a.keys()].filter((d) => b.has(d)).sort((x, y) => x - y);
  if (days.length < 10) return undefined;
  const ra: number[] = [];
  const rb: number[] = [];
  for (let i = 1; i < days.length; i++) {
    ra.push(Math.log(a.get(days[i])! / a.get(days[i - 1])!));
    rb.push(Math.log(b.get(days[i])! / b.get(days[i - 1])!));
  }
  const ma = ra.reduce((x, y) => x + y, 0) / ra.length;
  const mb = rb.reduce((x, y) => x + y, 0) / rb.length;
  let sab = 0;
  let saa = 0;
  let sbb = 0;
  for (let i = 0; i < ra.length; i++) {
    sab += (ra[i] - ma) * (rb[i] - mb);
    saa += (ra[i] - ma) ** 2;
    sbb += (rb[i] - mb) ** 2;
  }
  return sab / Math.sqrt(saa * sbb);
}

function runCOMP(ctx: Ctx): void {
  const fromArgs = ctx.args.filter((a) => !COMP_RANGE.test(a)).map((a) => resolve([a]));
  let syms = [...new Set([ctx.sec, ...fromArgs].filter((s): s is string => !!s))].slice(0, 8);
  if (syms.length < 2) syms = [...new Set([...syms, "^GSPC", "^IXIC"])].slice(0, Math.max(2, syms.length + 1));
  let range = ctx.args.find((a) => COMP_RANGE.test(a)) ?? "1Y";

  const toolbar = h("div", { class: "toolbar" });
  const wrap = h("div", { class: "chartwrap", style: { minHeight: "200px" } });
  const box = h("div", { class: "chartbox" });
  wrap.append(box);
  const tableBox = h("div", { style: { flex: "none", maxHeight: "40%", overflow: "auto" } });
  const add = h("input", { placeholder: "add ticker…", size: "12", spellcheck: "false" });
  add.addEventListener("keydown", (e) => {
    if (e.key !== "Enter") return;
    const s = resolve(add.value.toUpperCase().trim().split(/\s+/));
    add.value = "";
    if (s && !syms.includes(s) && syms.length < 8) {
      syms.push(s);
      load();
    }
  });
  const table = new DataTable<CompRow>(
    [
      { label: "", get: () => 0, sort: false, fmt: (_v, r) => h("span", { style: { color: r.color } }, "■") },
      { label: "Security", get: (r) => display(r.sym), cls: () => "amber" },
      { label: "Name", get: (r) => r.name, cls: () => "name" },
      { label: "Return", get: (r) => r.ret, fmt: (v) => f.pct(v), cls: (v) => f.cls(v), num: true },
      { label: "Vol (ann.)", get: (r) => r.vol, fmt: (v) => (f.num(v) === undefined ? f.NA : `${f.fixedN(v, 1)}%`), num: true },
      { label: "Max DD", get: (r) => r.dd, fmt: (v) => f.pct(v, 1), cls: () => "down", num: true },
      { label: `Corr`, get: (r) => r.corr, fmt: (v) => f.fixedN(v, 2), num: true, title: "Correlation of returns with the first security" },
      {
        label: "",
        get: () => 0,
        sort: false,
        fmt: (_v, r) =>
          syms.length > 1
            ? h("button", { title: "Remove", onclick: () => ((syms = syms.filter((s) => s !== r.sym)), load()) }, "×")
            : "",
      },
    ],
    { panel: ctx.panel, action: (r) => `${r.sym} DES` },
  );
  tableBox.append(table.el);
  const drawToolbar = () =>
    clear(toolbar, ...toggles(COMP_RANGES.map((r) => r[0]), range, (v) => ((range = v), drawToolbar(), load())), h("span", { class: "sep" }), add, h("span", { class: "dim" }, " up to 8 · COMP AAPL MSFT 5Y"));
  let chartAbort: AbortController | null = null;
  ctx.signal.addEventListener("abort", () => chartAbort?.abort());

  const load = async () => {
    const [, yr, interval] = COMP_RANGES.find((r) => r[0] === range)!;
    chartAbort?.abort();
    clear(box, loading());
    const results = await Promise.all(syms.map((s) => chart(s, yr, interval, ctx.signal).catch((e: Error) => e)));
    if (ctx.signal.aborted) return;
    const rows: CompRow[] = [];
    const errors: string[] = [];
    results.forEach((r, i) => {
      if (r instanceof Error) return errors.push(`${syms[i]}: ${r.message}`);
      const off = r.data.meta.gmtoffset ?? 0;
      rows.push({
        sym: syms[i],
        name: r.data.meta.longName ?? r.data.meta.shortName ?? "",
        color: COLORS.series[i % COLORS.series.length],
        closes: r.data.adj ?? r.data.c,
        times: r.data.t.map((t) => day(t, off)),
      });
    });
    const base = rows[0] ? new Map(rows[0].times.map((t, i) => [t, rows[0].closes[i]])) : null;
    const perYear = interval === "1d" ? 252 : interval === "1wk" ? 52 : 12;
    for (const r of rows) {
      r.ret = r.closes.length > 1 ? (r.closes[r.closes.length - 1] / r.closes[0] - 1) * 100 : undefined;
      r.vol = vol(r.closes, perYear);
      r.dd = maxDrawdown(r.closes);
      r.corr = base && r !== rows[0] ? correlation(base, new Map(r.times.map((t, i) => [t, r.closes[i]]))) : undefined;
    }
    table.set(rows);
    clear(box);
    chartAbort = new AbortController();
    const c = makeChart(box, chartAbort.signal, { timeScale: { timeVisible: false, borderColor: "#2e2e2e" } });
    for (const r of rows) {
      const s = c.addSeries(LineSeries, {
        color: r.color,
        lineWidth: 2,
        priceLineVisible: false,
        title: r.sym,
        priceFormat: { type: "custom", formatter: (p: number) => `${p > 0 ? "+" : ""}${p.toFixed(1)}%` },
      });
      const first = r.closes[0];
      s.setData(r.times.map((t, i) => ({ time: t as UTCTimestamp, value: (r.closes[i] / first - 1) * 100 })));
    }
    c.timeScale().fitContent();
    ctx.panel.status(`Yahoo · total return (adjusted closes) · ${range}${errors.length ? ` · failed: ${errors.join("; ")}` : ""}`);
  };
  drawToolbar();
  clear(ctx.body, h("div", { class: "fill" }, toolbar, wrap, tableBox));
  load().catch((e) => clear(box, errorBox(e)));
}

// --- Summary-based functions ------------------------------------------------

/// Load quoteSummary and hand it to `render`; equity-only modules may be
/// missing for funds, indices and foreign listings.
async function withSummary(ctx: Ctx, render: (s: Summary) => (HTMLElement | null)[]): Promise<void> {
  clear(ctx.body, loading());
  const r = await summary(ctx.sec!, ctx.signal);
  const parts = render(r.data).filter((x): x is HTMLElement => !!x);
  clear(ctx.body, h("div", { class: "pad" }, ...(parts.length ? parts : [h("div", { class: "dim" }, "No data for this security.")])));
  ctx.panel.status("Yahoo", r.meta);
}

const section = (title: string, ...body: (HTMLElement | null)[]) => h("div", null, h("h3", null, title), ...body);

// --- ANR --------------------------------------------------------------------

interface Trend {
  period: string;
  strongBuy: number;
  buy: number;
  hold: number;
  sell: number;
  strongSell: number;
}
interface Grade {
  epochGradeDate: number;
  firm: string;
  action: string;
  fromGrade: string;
  toGrade: string;
  priceTargetAction?: string;
  priorPriceTarget?: number;
  currentPriceTarget?: number;
}

const ACTIONS: Record<string, string> = { up: "Upgrade", down: "Downgrade", init: "Initiated", main: "Maintained", reit: "Reiterated" };
const REC_COLORS = ["#1fa84a", "#3ddc5f", "#999", "#ff8c5a", "#ff4b4b"];

function recBar(t: Trend, max: number): HTMLElement {
  const vals = [t.strongBuy, t.buy, t.hold, t.sell, t.strongSell];
  return h(
    "span",
    { style: { display: "inline-flex", width: "220px" } },
    ...vals.map((v, i) => h("span", { title: String(v), style: { width: `${(v / max) * 220}px`, height: "10px", background: REC_COLORS[i] } })),
  );
}

function runANR(ctx: Ctx): Promise<void> {
  return withSummary(ctx, (s) => {
    const fd = s.financialData ?? {};
    const px: number | undefined = fd.currentPrice ?? s.price?.regularMarketPrice;
    const trend: Trend[] = s.recommendationTrend?.trend ?? [];
    const grades: Grade[] = s.upgradeDowngradeHistory?.history ?? [];
    const max = Math.max(1, ...trend.map((t) => t.strongBuy + t.buy + t.hold + t.sell + t.strongSell));
    const periodName = (p: string) => (p === "0m" ? "Current" : `${p.replace("m", "")} mo`);

    const trendTable = new DataTable<Trend>(
      [
        { label: "Period", get: (t) => periodName(t.period), cls: () => "amber" },
        { label: "Strong buy", get: (t) => t.strongBuy, num: true },
        { label: "Buy", get: (t) => t.buy, num: true },
        { label: "Hold", get: (t) => t.hold, num: true },
        { label: "Sell", get: (t) => t.sell, num: true },
        { label: "Strong sell", get: (t) => t.strongSell, num: true },
        { label: "", get: () => 0, fmt: (_v, t) => recBar(t, max) },
      ],
      { sortable: false },
    );
    trendTable.set(trend);

    const gradeTable = new DataTable<Grade>(
      [
        { label: "Date", get: (g) => g.epochGradeDate, fmt: (v) => f.date(v), cls: () => "dim" },
        { label: "Firm", get: (g) => g.firm, cls: () => "amber" },
        { label: "Action", get: (g) => ACTIONS[g.action] ?? g.action, cls: (_v, g) => (g.action === "up" ? "up" : g.action === "down" ? "down" : "") },
        { label: "Rating", get: (g) => (g.fromGrade && g.fromGrade !== g.toGrade ? `${g.fromGrade} → ${g.toGrade}` : g.toGrade) },
        {
          label: "Target",
          get: (g) => g.currentPriceTarget || undefined,
          fmt: (_v, g) =>
            g.currentPriceTarget ? (g.priorPriceTarget && g.priorPriceTarget !== g.currentPriceTarget ? `${f.price(g.priorPriceTarget)} → ${f.price(g.currentPriceTarget)}` : f.price(g.currentPriceTarget)) : f.NA,
          cls: (_v, g) => (g.priceTargetAction === "Raises" ? "up" : g.priceTargetAction === "Lowers" ? "down" : ""),
          num: true,
        },
        { label: "vs price", get: (g) => (g.currentPriceTarget && px ? (g.currentPriceTarget / px - 1) * 100 : undefined), fmt: (v) => f.pct(v, 1), cls: (v) => f.cls(v), num: true },
      ],
      { sortable: true },
    );
    gradeTable.set(grades.slice(0, 80));

    const up = fd.targetMeanPrice && px ? (fd.targetMeanPrice / px - 1) * 100 : undefined;
    const counts = { up: 0, down: 0 };
    const since = Date.now() / 1000 - 90 * 86400;
    for (const g of grades) if (g.epochGradeDate > since && (g.action === "up" || g.action === "down")) counts[g.action]++;

    return [
      h(
        "div",
        { class: "cols" },
        section(
          "Consensus",
          kv([
            ["Rating", fd.recommendationKey && fd.recommendationKey !== "none" ? String(fd.recommendationKey).toUpperCase().replace("_", " ") : null],
            ["Mean score", fd.recommendationMean ? `${f.fixedN(fd.recommendationMean)}  (1 strong buy … 5 sell)` : null],
            ["Analysts", f.int(fd.numberOfAnalystOpinions)],
            ["Last 90 days", `${counts.up} upgrades, ${counts.down} downgrades`],
          ]),
        ),
        section(
          "Price targets",
          kv([
            ["Current price", f.price(px)],
            ["Mean", fd.targetMeanPrice ? h("span", null, f.price(fd.targetMeanPrice), " ", h("span", { class: f.cls(up) }, f.pct(up, 1))) : null],
            ["Median", f.price(fd.targetMedianPrice)],
            ["Low – high", fd.targetLowPrice ? `${f.price(fd.targetLowPrice)} – ${f.price(fd.targetHighPrice)}` : null],
            ["Price in range", fd.targetLowPrice ? rangeBar(fd.targetLowPrice, fd.targetHighPrice, px, 120) : null],
          ]),
        ),
      ),
      trend.length ? section("Recommendation trend", trendTable.el) : null,
      grades.length ? section("Rating changes", gradeTable.el) : null,
    ];
  });
}

// --- HDS --------------------------------------------------------------------

interface Holder {
  organization: string;
  pctHeld?: number;
  position?: number;
  value?: number;
  pctChange?: number;
  reportDate?: number;
}
interface InsiderTx {
  startDate?: number;
  filerName?: string;
  filerRelation?: string;
  transactionText?: string;
  shares?: number;
  value?: number;
  ownership?: string;
}

function holderTable(list: Holder[]): HTMLElement {
  const t = new DataTable<Holder>([
    { label: "Holder", get: (x) => x.organization, cls: () => "amber" },
    { label: "Shares", get: (x) => x.position, fmt: (v) => f.big(v), num: true },
    { label: "% out", get: (x) => x.pctHeld, fmt: (v) => f.ratio(v), num: true },
    { label: "Value", get: (x) => x.value, fmt: (v) => f.big(v), num: true },
    { label: "Chg", get: (x) => x.pctChange, fmt: (v) => (f.num(v) === undefined ? f.NA : f.pct((v as number) * 100, 1)), cls: (v) => f.cls(v), num: true },
    { label: "Reported", get: (x) => x.reportDate, fmt: (v) => f.utcDate(v), cls: () => "dim", num: true },
  ]);
  t.set(list);
  return t.el;
}

function runHDS(ctx: Ctx): Promise<void> {
  return withSummary(ctx, (s) => {
    const mh = s.majorHoldersBreakdown ?? {};
    const ns = s.netSharePurchaseActivity ?? {};
    const inst: Holder[] = s.institutionOwnership?.ownershipList ?? [];
    const funds: Holder[] = s.fundOwnership?.ownershipList ?? [];
    const txs: InsiderTx[] = s.insiderTransactions?.transactions ?? [];
    const txTable = new DataTable<InsiderTx>([
      { label: "Date", get: (x) => x.startDate, fmt: (v) => f.utcDate(v), cls: () => "dim" },
      { label: "Insider", get: (x) => x.filerName, cls: () => "amber" },
      { label: "Relation", get: (x) => x.filerRelation },
      {
        label: "Transaction",
        get: (x) => x.transactionText || "Award/gift/other",
        cls: (v: string) => (/^Sale/.test(v) ? "down" : /^Purchase|^Buy/.test(v) ? "up" : ""),
      },
      { label: "Shares", get: (x) => x.shares, fmt: (v) => f.int(v), num: true },
      { label: "Value", get: (x) => x.value || undefined, fmt: (v) => f.big(v), num: true },
    ]);
    txTable.set(txs);
    return [
      h(
        "div",
        { class: "cols" },
        section(
          "Ownership",
          kv([
            ["Institutions", f.ratio(mh.institutionsPercentHeld)],
            ["…of float", f.ratio(mh.institutionsFloatPercentHeld)],
            ["Institution count", f.int(mh.institutionsCount)],
            ["Insiders", f.ratio(mh.insidersPercentHeld)],
          ]),
        ),
        ns.period
          ? section(
              `Insider activity (${ns.period})`,
              kv([
                ["Buys", `${f.int(ns.buyInfoCount)} txns, ${f.big(ns.buyInfoShares)} sh`],
                ["Sells", `${f.int(ns.sellInfoCount)} txns, ${f.big(ns.sellInfoShares)} sh`],
                ["Net", h("span", { class: f.cls(ns.netInfoShares) }, `${f.big(ns.netInfoShares)} sh`)],
                ["Insider shares", f.big(ns.totalInsiderShares)],
              ]),
            )
          : null,
      ),
      inst.length ? section("Top institutional holders", holderTable(inst)) : null,
      funds.length ? section("Top fund holders", holderTable(funds)) : null,
      txs.length ? section("Insider transactions", txTable.el) : null,
    ];
  });
}

// --- EE ---------------------------------------------------------------------

interface Surprise {
  quarter?: number;
  period?: string;
  epsEstimate?: number;
  epsActual?: number;
  epsDifference?: number;
  surprisePercent?: number;
}
interface Estimate {
  period: string;
  endDate?: string;
  growth?: number;
  earningsEstimate?: { avg?: number; low?: number; high?: number; numberOfAnalysts?: number; yearAgoEps?: number; growth?: number };
  revenueEstimate?: { avg?: number; low?: number; high?: number; numberOfAnalysts?: number; yearAgoRevenue?: number; growth?: number };
  epsTrend?: Record<string, number | undefined>;
  epsRevisions?: Record<string, number | undefined>;
}
interface FinYear {
  date: string | number;
  revenue?: number;
  earnings?: number;
}

const PERIODS: Record<string, string> = { "0q": "Current qtr", "+1q": "Next qtr", "0y": "Current year", "+1y": "Next year" };

function runEE(ctx: Ctx): Promise<void> {
  return withSummary(ctx, (s) => {
    const hist: Surprise[] = s.earningsHistory?.history ?? [];
    const trend: Estimate[] = (s.earningsTrend?.trend ?? []).filter((t: Estimate) => PERIODS[t.period]);
    const ce = s.calendarEvents?.earnings ?? {};
    const yearly: FinYear[] = s.earnings?.financialsChart?.yearly ?? [];
    const quarterly: FinYear[] = s.earnings?.financialsChart?.quarterly ?? [];

    const surprises = new DataTable<Surprise>(
      [
        { label: "Quarter end", get: (x) => x.quarter, fmt: (v) => f.utcDate(v), cls: () => "amber" },
        { label: "Estimate", get: (x) => x.epsEstimate, fmt: (v) => f.price(v), num: true },
        { label: "Actual", get: (x) => x.epsActual, fmt: (v) => f.price(v), num: true },
        { label: "Diff", get: (x) => x.epsDifference, fmt: (v) => f.chg(v), cls: (v) => f.cls(v), num: true },
        { label: "Surprise", get: (x) => x.surprisePercent, fmt: (v) => (f.num(v) === undefined ? f.NA : f.pct((v as number) * 100, 1)), cls: (v) => f.cls(v), num: true },
      ],
      { sortable: false },
    );
    surprises.set([...hist].reverse());

    const est = new DataTable<Estimate>(
      [
        { label: "Period", get: (t) => PERIODS[t.period], cls: () => "amber" },
        { label: "Ends", get: (t) => t.endDate ?? "", cls: () => "dim" },
        { label: "EPS est", get: (t) => t.earningsEstimate?.avg, fmt: (v) => f.price(v), num: true },
        { label: "Low – high", get: (t) => t.earningsEstimate?.low, fmt: (_v, t) => (t.earningsEstimate?.low !== undefined ? `${f.price(t.earningsEstimate.low)} – ${f.price(t.earningsEstimate.high)}` : f.NA), num: true },
        { label: "Yr ago", get: (t) => t.earningsEstimate?.yearAgoEps, fmt: (v) => f.price(v), num: true },
        { label: "Growth", get: (t) => t.earningsEstimate?.growth, fmt: (v) => (f.num(v) === undefined ? f.NA : f.pct((v as number) * 100, 1)), cls: (v) => f.cls(v), num: true },
        { label: "Analysts", get: (t) => t.earningsEstimate?.numberOfAnalysts, num: true },
        { label: "Revenue est", get: (t) => t.revenueEstimate?.avg, fmt: (v) => f.big(v), num: true },
        { label: "Rev growth", get: (t) => t.revenueEstimate?.growth, fmt: (v) => (f.num(v) === undefined ? f.NA : f.pct((v as number) * 100, 1)), cls: (v) => f.cls(v), num: true },
        {
          label: "EPS 90d ago",
          get: (t) => t.epsTrend?.["90daysAgo"],
          fmt: (v, t) => {
            const now = t.epsTrend?.current;
            const pct = f.num(v) && now ? (now / (v as number) - 1) * 100 : undefined;
            return h("span", null, f.price(v), " ", h("span", { class: f.cls(pct) }, pct === undefined ? "" : f.pct(pct, 1)));
          },
          num: true,
          title: "Consensus 90 days ago and how far it has moved since",
        },
        { label: "Revisions 30d", get: (t) => (t.epsRevisions?.upLast30days ?? 0) - (t.epsRevisions?.downLast30days ?? 0), fmt: (_v, t) => `↑${t.epsRevisions?.upLast30days ?? 0} ↓${t.epsRevisions?.downLast30days ?? 0}`, num: true },
      ],
      { sortable: false },
    );
    est.set(trend);

    const fin = (rows: FinYear[]) => {
      const max = Math.max(1, ...rows.map((r) => Math.abs(r.revenue ?? 0)));
      const t = new DataTable<FinYear>(
        [
          { label: "Period", get: (r) => String(r.date), cls: () => "amber" },
          { label: "Revenue", get: (r) => r.revenue, fmt: (v) => f.big(v), num: true },
          { label: "", get: (r) => r.revenue, fmt: (v) => h("span", { class: "bar", style: { width: `${((f.num(v) ?? 0) / max) * 120}px` } }), sort: false },
          { label: "Earnings", get: (r) => r.earnings, fmt: (v) => f.big(v), cls: (v) => f.cls(v), num: true },
          { label: "Margin", get: (r) => (r.revenue && r.earnings !== undefined ? (r.earnings / r.revenue) * 100 : undefined), fmt: (v) => (f.num(v) === undefined ? f.NA : `${f.fixedN(v, 1)}%`), num: true },
        ],
        { sortable: false },
      );
      t.set(rows);
      return t.el;
    };

    const next = ce.earningsDate?.[0];
    return [
      section(
        "Next report",
        kv([
          ["Date", next ? `${f.date(next)}${ce.isEarningsDateEstimate ? " (estimated)" : ""}` : null],
          ["EPS estimate", ce.earningsAverage !== undefined ? `${f.price(ce.earningsAverage)} (${f.price(ce.earningsLow)} – ${f.price(ce.earningsHigh)})` : null],
          ["Revenue estimate", ce.revenueAverage ? `${f.big(ce.revenueAverage)} (${f.big(ce.revenueLow)} – ${f.big(ce.revenueHigh)})` : null],
        ]),
      ),
      hist.length ? section("EPS surprises", surprises.el) : null,
      trend.length ? section("Estimates", est.el) : null,
      yearly.length || quarterly.length
        ? h("div", { class: "cols" }, yearly.length ? section("Annual", fin(yearly)) : null, quarterly.length ? section("Quarterly", fin(quarterly)) : null)
        : null,
    ];
  });
}

register(
  {
    code: "HP",
    name: "Historical Prices",
    group: "Equity",
    needsSec: true,
    args: /^(1M|3M|6M|YTD|1Y|2Y|5Y|10Y|MAX)$/,
    help: "Daily/weekly/monthly OHLCV table with stats and CSV export.",
    run: runHP,
  },
  {
    code: "COMP",
    name: "Comparative Returns",
    group: "Equity",
    needsSec: false,
    args: /^.+$/,
    help: "Total return of up to 8 securities on one chart. COMP AAPL MSFT NVDA 2Y.",
    aliases: ["RR"],
    run: runCOMP,
  },
  {
    code: "ANR",
    name: "Analyst Recommendations",
    group: "News & Research",
    needsSec: true,
    help: "Consensus rating, price targets, recommendation trend, rating changes.",
    run: runANR,
  },
  {
    code: "HDS",
    name: "Holders",
    group: "Equity",
    needsSec: true,
    help: "Institutional and fund holders, insider transactions.",
    run: runHDS,
  },
  {
    code: "EE",
    name: "Earnings & Estimates",
    group: "Equity",
    needsSec: true,
    help: "EPS surprises, consensus estimates and revisions, revenue and earnings history.",
    aliases: ["EM"],
    run: runEE,
  },
);
